<template>
    <div
        :key="widgetGroup.WidgetGroupID"
        class="flex flex-col h-full w-full pb-2"
        data-testid="widget-list-container"
        :style="gridLayoutStyle"
    >
        <ChatsContainer class="absolute" />
        <div
            :id="gridId"
            :key="gridId"
            ref="widgetListContainer"
            data-testid="widget-grid"
            :class="getClass"
        >
            <div
                v-for="(widget) in widgets"
                v-bind="gridStackAttributes(widget)"
                :key="`widget_wrapper-${widget.WidgetID}`"
                :data-id="widget.WidgetID"
                class="grid-stack-item"
                @mousedown="onMousedown"
            >
                <WidgetErrorBoundary>
                    <Widget
                        :editable="editable"
                        :grid-edit-mode="gridEditMode"
                        :in-view-by-id="inViewById"
                        :widget="widget"
                        :show-widget-content="layoutType === layoutTypes.LIST || widgetIdsToShow.includes(widget.WidgetID)"
                        :preview-mode="previewMode"
                        :layout-type="layoutType"
                        @remove-item="removeWidget"
                        @update-item="updateWidget"
                        @duplicate-widget="emit('duplicate-widget', $event)"
                    />
                </WidgetErrorBoundary>
            </div>
        </div>
        <div
            v-if="widgets.length === 0"
            :key="`no-data-${widgetGroup.WidgetGroupID}`"
            class="w-full flex flex-col items-center mt-20"
            data-testid="widget-list-no-data"
        >
            <div
                class="flex flex-col items-center"
            >
                <icon-no-data-cry class="h-61 w-61" />
                <RedesignBaseButton
                    icon="PlusCircle"
                    size="large"
                    @click="emit('add-widget', widgetGroup)"
                >
                    {{ $t('dashboard.addNewWidgets') }}
                </RedesignBaseButton>
                <p class="main-text-color pt-5 max-w-lg text-center">
                    {{ $t('dashboard.widgetGroupNoData') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { debouncedWatch, useElementSize } from '@vueuse/core'

import Widget from './Widget.vue'

import WidgetErrorBoundary from '@/components/WidgetErrorBoundary.vue'
import { layoutTypes } from '@/enum/layout'
import { ExtendedWidgetGroupList, WidgetGroupList, WidgetList } from '@/types/dashboard'
import { isRTL } from '@/composables/useDirection'
import useGridStack from '@/composables/useGridStack'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import ChatsContainer from '@/components/RedesignComponents/Cards/Voicebots/ChatsContainer.vue'

/* Types */
export type RemoveWidgetEmitPayload = {
    widget: WidgetList
    group: WidgetGroupList
}
export type UpdateWidgetEmitPayload = {
    widget: WidgetList
    group: WidgetGroupList
    silence: boolean
}

/* Props */
const props = withDefaults(
    defineProps<{
        widgetGroup: ExtendedWidgetGroupList,
        editable?: boolean,
        gridEditMode?: boolean,
        previewMode?: boolean
        layoutType?: string
    }>(),
    {
        editable: false,
        gridEditMode: false,
        previewMode: false,
        layoutType: ''
    }
)

/* Emit */
const emit = defineEmits<{
    (e: 'remove-widget', payload: RemoveWidgetEmitPayload): void
    (e: 'update-widget', payload: UpdateWidgetEmitPayload): void
    (e: 'duplicate-widget', payload: WidgetList): void
    (e: 'add-widget', payload: ExtendedWidgetGroupList): void
}>()

/* Data */
const widgetListContainer = ref<HTMLElement>()
let observer: null | IntersectionObserver = null
const inViewById = ref<Record<number, boolean>>({})
const widgetIdsToShow = ref<Array<number>>([])

/* Composable */
const { width } = useElementSize(widgetListContainer)
const { initWindowGrid, getCurrentGrid, makeWidget } = useGridStack(props.widgetGroup.WidgetGroupID)

/* Computed */
const getClass = computed(() => {
    return `grid-stack grid-stack-instance-${props.widgetGroup.WidgetGroupID}`
})
const gridId = computed(() => {
    return `grid-stack-${props.widgetGroup.WidgetGroupID}`
})
const widgets = computed(() => {
    return props.widgetGroup.WidgetList ?? []
})
const widgetsAndWidgetsIDsToShow = computed(() => {
    return widgets.value.map(({
        WidgetID,
        loadedFromCache
    }) => `${WidgetID}_${loadedFromCache}|${widgetIdsToShow.value}`).join('')
})
const gridLayoutStyle = computed(() => {
    if (!props.editable) {
        return
    }

    const gridGap = (width.value * 1.5625) / 100
    const rounded = Math.round((gridGap + Number.EPSILON) * 100) / 100
    return {
        'background-image': 'radial-gradient(circle, var(--primary-color) 1px, transparent 0)',
        'background-size': `${rounded}px 40px`,
        'background-position': '0 0'
    }
})

/* Methods */
function gridStackAttributes (widget: WidgetList) {
    return {
        id: widget.WidgetID,
        'data-gs-id': widget.WidgetID,
        'data-gs-x': widget.WidgetLayout?.GridLayout?.x ?? 0,
        'data-gs-y': widget.WidgetLayout?.GridLayout?.y ?? 2,
        'data-gs-width': widget.WidgetLayout?.GridLayout?.width ?? 64,
        'data-gs-height': widget.WidgetLayout?.GridLayout?.height ?? 4
    }
}

function removeWidget (widget: WidgetList) {
    emit(
        'remove-widget',
        {
            widget: widget,
            group: props.widgetGroup
        }
    )
}

function updateWidget (widget: WidgetList, silence: boolean) {
    emit(
        'update-widget',
        {
            widget: widget,
            group: props.widgetGroup,
            silence
        }
    )
}

function onDragStartEvent () {
    const currentGrid = getCurrentGrid()

    currentGrid?.on('dragstart', onMousedown)
}

function onMousedown () {
    const currentGrid = getCurrentGrid()

    const preventMovable = !!(isWidgetModalOpen() || !props.editable)
    currentGrid?.movable('.grid-stack-item', !preventMovable)
}

function isWidgetModalOpen () {
    const bodyElement = document.body
    if (bodyElement.classList.length) {
        return false
    }
    return bodyElement.classList.contains('el-popup-parent--hidden')
}

function initIntersectionObserver () {
    const sections = document.querySelectorAll('.grid-stack-item')

    observer = new IntersectionObserver(
        (entries, observer) => {
            entries.forEach((entry) => {
                if (!(entry.target instanceof HTMLElement)) {
                    return
                }

                const id = parseInt(entry.target.dataset.id ?? '')

                if (isNaN(id)) {
                    return
                }

                if (!entry.isIntersecting) {
                    inViewById.value[id] = entry.isIntersecting
                } else {
                    inViewById.value[id] = entry.isIntersecting
                    observer.unobserve(entry.target)
                }
            })
        },
        {
            root: null,
            threshold: 0.1,
            rootMargin: '200px',
        }
    )

    sections.forEach((section) => {
        observer?.observe(section)
    })
}

function showNextWidget () {
    const sortedWidgetsToShow = widgets.value
        .filter(({ loadedFromCache, WidgetID }) => !loadedFromCache && !widgetIdsToShow.value.includes(WidgetID))
        .sort((a, b) => {
            const aY = a.WidgetLayout?.GridLayout?.y ?? 0
            const bY = b.WidgetLayout?.GridLayout?.y ?? 0

            return aY - bY
        })

    if (sortedWidgetsToShow.length > 0) {
        widgetIdsToShow.value.push(sortedWidgetsToShow[0].WidgetID)
    }
}

/* Hooks */
onMounted(
    async () => {
        await nextTick()
        if (widgetListContainer.value) {
            initWindowGrid(widgetListContainer.value, props.editable)
        }

        await nextTick()

        onDragStartEvent()
        initIntersectionObserver()
    }
)
onBeforeUnmount(
    () => {
        observer?.disconnect()
    }
)

/* Watch */
watch(
    () => props.editable,
    (value) => {
        const currentGrid = getCurrentGrid()

        currentGrid?.movable('.grid-stack-item', value)
        currentGrid?.resizable('.grid-stack-item', value)
    }
)
watch(
    isRTL,
    (value) => {
        const currentGrid = getCurrentGrid()

        if (currentGrid) {
            currentGrid.rtl = value
        }
    }
)
debouncedWatch(
    widgets,
    async (newVal, oldVal) => {
        const addedWidgets = newVal.filter(({ WidgetID }) => !oldVal.some(({ WidgetID: oldWidgetID }) => oldWidgetID === WidgetID))

        await nextTick()

        addedWidgets.forEach((widget) => {
            makeWidget(widget.WidgetID)
        })
    },
    {
        debounce: 300
    }
)
debouncedWatch(
    widgetsAndWidgetsIDsToShow,
    showNextWidget,
    {
        immediate: true,
        debounce: 300
    }
)
</script>
